/**
 * ContactDetailPanel.jsx - one contact in full (C-01): who they are, where
 * they sit, everything logged with them and every task that names them.
 * Paul: "Opening Hong should show everything involving Hong, including the
 * tasks about him."
 *
 * Opens in place, three ways (one component):
 *   - inside AccountDetailPanel, from a name on its Contacts tab (Back returns
 *     to that tab);
 *   - in CRMView, from a row of the Contacts directory;
 *   - by deep link, #/crm?contact=<id> (CRMView reads it).
 *
 * The contact comes from the shared store (useApp().allAccounts, every nested
 * row camelCased), so an edit here, a move (C-02) or a status change (C-04)
 * shows everywhere after the fetchAll. contactIndex does not drop anyone who
 * left the company - their record and history stay reachable.
 *
 *   - Activity: the contact timeline (entries that list this contact as an
 *     attendee, plus the events of tasks that name them). Log Activity files
 *     the entry on the contact's own account or site, with the contact
 *     already ticked as an attendee. The timeline re-reads itself when a task
 *     changes, so a task completed below shows as "Task completed" at once.
 *   - Tasks: GET /api/crm-actions?contact_id=<id>, completed in place through
 *     useCompleteTask so the next-step rule (T-04) still runs. There is no
 *     "Add task" here - tasks are not attached to contacts (T-02); they live
 *     on a lead site or an opportunity and may name a contact.
 *
 * Also exports ContactFields, the one contact form (add, edit, move) that the
 * account panel's Contacts tab and the Contacts directory use too.
 *
 * Never shown on the GLOBAL KSA tab (it has no CRM contacts): in scope it
 * renders nothing and fetches nothing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../ui/Icon';
import { Toast } from '../ui/Toast';
import { useApp } from '../../context/AppContext';
import { useApiBase, useDataScope } from '../../context/DataScope';
import { CONTACT_ROLES, CONTACT_STATUSES, LEFT_COMPANY, isLeftCompany, pickField } from '../../data/crmStages';
import { fmtDate } from '../../utils/format';
import AccountSitePicker from './AccountSitePicker';
import Timeline from './Timeline';
import ActivityForm from './ActivityForm';
import { contactPayload, contactFormFrom, linkedinHref } from './crmFormHelpers';
import { INPUT, LABEL, BTN_PRIMARY, BTN_CANCEL, ERROR_LINE, tagStyle } from './crmStyles';
import { accountLabel, contactIndex, homeOfTask, indexById, taskHash } from './nextStep';
import { useCompleteTask } from '../tasks/useCompleteTask';
import { useTasksChanged } from '../tasks/useTasks';
import { localDay } from '../tasks/tasksTable';
import { TaggedNames } from '../tasks/TagPeople';
import { taggedOf } from '../tasks/tagged';
import { latestReads } from '../../live/useLive';

/** The grey tag a contact who left the company wears in every list (C-04). */
export const LEFT_TAG = tagStyle('#616161', '#EEEEEE');
const PRIMARY_TAG = tagStyle('#1B5E20', '#E8F5E9');

const dayOf = (v) => String(v || '').slice(0, 10);
// a TIMESTAMPTZ (completed_at) as its LOCAL day: its first ten characters are the UTC day,
// so an evening completion in the US read as the next day
const localDayOf = (v) => {
  const d = new Date(v);
  return v && !isNaN(d.getTime()) ? localDay(d) : dayOf(v);
};
const initialsOf = (name) =>
  String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';
const readError = async (r, fallback) => {
  const b = await r.json().catch(() => ({}));
  return b.error || fallback;
};

/**
 * The one contact form (C-02 / C-03 / C-04 / C-05): Name, Title, Role, Email,
 * Phone, LinkedIn URL, Contact status, an optional account / site picker and
 * the Primary box. Marking someone Left company unticks Primary and locks it -
 * a contact who left can't be anyone's first call.
 *
 * picker: null (no picker) or { label, placeholder, first } - `first` puts it
 * ahead of Name (the directory's Add form asks where the contact goes first).
 * onEdited runs after any change (the hosts clear their error line with it).
 */
export function ContactFields({ form, setForm, accounts = [], picker = null, primaryLabel, onEdited }) {
  const touch = () => {
    if (onEdited) onEdited();
  };
  const set = (key) => (e) => {
    const v = e.target.value;
    setForm((p) => ({ ...p, [key]: v }));
    touch();
  };
  const left = form.contactStatus === LEFT_COMPANY;
  const pickerEl = picker ? (
    <div>
      <label style={LABEL}>{picker.label}</label>
      <AccountSitePicker
        accounts={accounts}
        value={form.accountId}
        style={INPUT}
        placeholder={picker.placeholder}
        onChange={(v) => {
          // a contact moved elsewhere is not that account's primary until the box is ticked again
          setForm((p) => ({ ...p, accountId: v || p.accountId, isPrimary: p.accountId && v && v !== p.accountId ? false : p.isPrimary }));
          touch();
        }}
      />
    </div>
  ) : null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
      {picker && picker.first && pickerEl}
      <div>
        <label style={LABEL}>Name</label>
        <input style={INPUT} value={form.name || ''} onChange={set('name')} placeholder="Full name" />
      </div>
      <div>
        <label style={LABEL}>Title</label>
        <input style={INPUT} value={form.role || ''} onChange={set('role')} placeholder="e.g. Plant Manager" />
      </div>
      <div>
        <label style={LABEL}>Role</label>
        <select style={{ ...INPUT, appearance: 'auto' }} value={form.contactRole || ''} onChange={set('contactRole')}>
          <option value="">-</option>
          {CONTACT_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
          {form.contactRole && !CONTACT_ROLES.includes(form.contactRole) && <option value={form.contactRole}>{form.contactRole}</option>}
        </select>
      </div>
      <div>
        <label style={LABEL}>Email</label>
        <input style={INPUT} value={form.email || ''} onChange={set('email')} placeholder="email@example.com" />
      </div>
      <div>
        <label style={LABEL}>Phone</label>
        <input style={INPUT} value={form.phone || ''} onChange={set('phone')} placeholder="+1 (555) 000-0000" />
      </div>
      <div>
        <label style={LABEL}>LinkedIn URL</label>
        <input style={INPUT} value={form.linkedinUrl || ''} onChange={set('linkedinUrl')} placeholder="linkedin.com/in/..." />
      </div>
      {/* C-04: someone who left keeps their history but drops out of every picker */}
      <div>
        <label style={LABEL}>Contact status</label>
        <select
          style={{ ...INPUT, appearance: 'auto' }}
          value={left ? LEFT_COMPANY : 'Active'}
          onChange={(e) => {
            const v = e.target.value;
            setForm((p) => ({ ...p, contactStatus: v, isPrimary: v === LEFT_COMPANY ? false : p.isPrimary }));
            touch();
          }}
        >
          {CONTACT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      {picker && !picker.first && pickerEl}
      <label
        title={left ? "A contact who left the company can't be the primary contact" : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 11,
          color: '#444',
          fontWeight: 600,
          cursor: left ? 'not-allowed' : 'pointer',
          alignSelf: 'end',
          paddingBottom: 8,
          opacity: left ? 0.5 : 1,
        }}
      >
        <input
          type="checkbox"
          checked={!left && !!form.isPrimary}
          disabled={left}
          onChange={(e) => {
            const v = e.target.checked;
            setForm((p) => ({ ...p, isPrimary: v }));
            touch();
          }}
        />
        {primaryLabel}
      </label>
    </div>
  );
}

const card = { background: '#fff', border: '1px solid #ECECEC', borderRadius: 12, padding: '14px 18px' };
const cardTitle = { fontSize: 11, fontWeight: 800, color: '#1B5E20', letterSpacing: 1.4, textTransform: 'uppercase' };
const linkBtn = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontSize: 'inherit',
  fontWeight: 700,
  color: '#1565C0',
  fontFamily: 'Montserrat',
};

/* ===== Contact Detail Panel ===== */
export default function ContactDetailPanel({ contactId, onBack, backLabel = 'Back', editable = false, deletable = false, onOpenAccount }) {
  const { allAccounts, allProjects, fetchAll } = useApp();
  const scope = useDataScope();
  const base = useApiBase();
  const id = String(contactId == null ? '' : contactId);

  const contacts = useMemo(() => contactIndex(allAccounts), [allAccounts]);
  const accountsById = useMemo(() => indexById(allAccounts), [allAccounts]);
  const projectsById = useMemo(() => indexById(allProjects), [allProjects]);
  const contact = contacts.get(id) || null;
  const homeId = contact ? pickField(contact, 'accountId', 'account_id') : null;
  const home = homeId ? accountsById.get(String(homeId)) || null : null;
  const parentId = home ? pickField(home, 'parentAccountId', 'parent_account_id') : null;
  const parent = parentId ? accountsById.get(String(parentId)) || null : null;
  // a company with sites takes no tasks, so the Log Activity form offers no next step there (Paul Q12)
  const homeHasSites =
    !!home &&
    !parentId &&
    ((home.siteCount || 0) > 0 || (allAccounts || []).some((a) => pickField(a, 'parentAccountId', 'parent_account_id') === home.id));

  const [editingForm, setEditingForm] = useState(null); // contactFormFrom(contact) while editing
  const [editErr, setEditErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [logging, setLogging] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [tasksErr, setTasksErr] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [toast, setToastState] = useState(null); // { message, tone }
  const setToast = (message, tone) => setToastState(message ? { message, tone } : null);
  const { complete, prompt } = useCompleteTask();

  // the tasks that name this contact; a failed load says so instead of showing "none"
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  // Its load, a re-read after a change here and a live one (someone else's
  // change) can overlap: the newest answer GOT wins. A read that fails keeps
  // the tasks on screen, and only one the person is waiting for says so.
  const [taskReads] = useState(latestReads);
  const loadTasks = useCallback(
    ({ live } = {}) => {
      if (scope || !id) return;
      const ticket = taskReads.start();
      const fallback = 'Could not load the tasks that name this contact - try again.';
      fetch(`${base}/crm-actions?contact_id=${encodeURIComponent(id)}`)
        .then(async (r) => {
          if (!r.ok) throw new Error(await readError(r, fallback));
          const rows = await r.json();
          if (!Array.isArray(rows)) throw new Error(fallback);
          return rows;
        })
        .then((rows) => {
          if (!alive.current || !taskReads.show(ticket)) return;
          // only the tasks that name this contact, even from a server that ignores the filter
          setTasks(rows.filter((t) => t && String(t.contact_id) === id));
          setTasksErr('');
          setTasksLoaded(true);
        })
        .catch((err) => {
          if (!alive.current) return;
          console.error('contact tasks error:', err);
          if (live || !taskReads.show(ticket)) return;
          setTasksErr((err && err.message) || fallback);
          setTasksLoaded(true);
        });
    },
    [scope, id, base, taskReads]
  );
  // another contact: their own tasks only - a read still out for the last one never lands here
  useEffect(() => {
    taskReads.show(taskReads.start());
    setTasks([]);
    setTasksErr('');
    setTasksLoaded(false);
    loadTasks();
  }, [loadTasks, taskReads]);
  // completed on the Dashboard, the Tasks page or a lead panel: reload here too
  useTasksChanged(loadTasks);

  if (scope) return null;

  // a real button, so the keyboard can leave the page too
  const back = (
    <button
      type="button"
      onClick={onBack}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        marginBottom: 16,
        width: 'fit-content',
        background: 'transparent',
        border: 'none',
        padding: 0,
        fontFamily: 'Montserrat',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '0.7';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '1';
      }}
    >
      <span style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}>
        <Icon name="chevron" size={16} color="#555" />
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>{backLabel}</span>
    </button>
  );

  if (!contact) {
    return (
      <div>
        {back}
        <div style={{ ...card, textAlign: 'center', padding: 40, color: '#888', fontSize: 12 }}>
          This contact no longer exists. It may have been deleted.
        </div>
      </div>
    );
  }

  const left = isLeftCompany(contact);
  const primary = !!pickField(contact, 'isPrimary', 'is_primary');
  const title = contact.role && contact.role !== '-' ? contact.role : '';
  const contactRole = pickField(contact, 'contactRole', 'contact_role');
  const linkedin = pickField(contact, 'linkedinUrl', 'linkedin_url');
  const email = contact.email && contact.email !== '-' ? contact.email : '';
  const phone = contact.phone && contact.phone !== '-' ? contact.phone : '';
  const homeName = home ? accountLabel(home, accountsById) : contact.accountName || 'its account';
  const openAcct = (a) => {
    if (a && onOpenAccount) onOpenAccount(a);
  };

  // C-02: a different account / site in the form moves the contact there. The
  // URL must name the contact's CURRENT account (the API finds it there).
  const saveEdit = async () => {
    const f = editingForm;
    if (!f || saving) return;
    if (!String(f.name || '').trim()) {
      setEditErr("Enter the contact's name.");
      return;
    }
    const moveTo = f.accountId && String(f.accountId) !== String(homeId) ? f.accountId : null;
    setSaving(true);
    try {
      const r = await fetch(`/api/accounts/${encodeURIComponent(homeId)}/contacts/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contactPayload(f), ...(moveTo ? { account_id: moveTo } : {}) }),
      });
      if (!r.ok) {
        setEditErr(await readError(r, 'Could not save the contact - try again.'));
        return;
      }
      await fetchAll();
    } catch (err) {
      console.error('editContact error:', err);
      setEditErr('Could not save the contact - try again.');
      return;
    } finally {
      setSaving(false);
    }
    setEditingForm(null);
    setEditErr('');
    const dest = moveTo && accountsById.get(String(moveTo));
    setToast(dest ? `${f.name.trim()} moved to ${dest.name}` : 'Contact updated');
  };

  const onLogged = (row) => {
    setLogging(false);
    setRefreshKey((k) => k + 1);
    fetchAll();
    setToast(row && (row.next_step || row.nextStep) ? 'Activity logged - next step added to Tasks' : 'Activity logged');
  };

  // the LOCAL day: a UTC "today" made tasks due today overdue after about 8pm Eastern
  const today = localDay();
  const open = tasks.filter((t) => t.status === 'open');
  const done = tasks.filter((t) => t.status !== 'open');
  const homeLink = (t) => {
    const h = homeOfTask(t, accountsById, projectsById);
    const label = (h && h.label) || t.entity_label || '';
    if (!label) return null;
    const pill = { fontSize: 9.5, fontWeight: 700, whiteSpace: 'nowrap' };
    if (h && h.kind === 'lead' && onOpenAccount) {
      return (
        <button onClick={() => openAcct(h.record)} title={`Open ${label}`} style={{ ...linkBtn, ...pill, color: '#7B1FA2' }}>
          {label}
        </button>
      );
    }
    const href = taskHash(t, projectsById);
    return href ? (
      <a href={href} title={`Open ${label}`} style={{ ...pill, color: '#7B1FA2', textDecoration: 'none' }}>
        {label}
      </a>
    ) : (
      <span style={{ ...pill, color: '#7B1FA2' }}>{label}</span>
    );
  };

  return (
    <div>
      {toast && <Toast message={toast.message} tone={toast.tone} onDone={() => setToast(null)} />}
      {back}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* HEADER */}
        <div style={{ ...card, padding: '22px 26px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: left ? '#9E9E9E' : '#0D0D0D',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: left ? '#fff' : '#43A047',
                fontSize: 16,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {initialsOf(contact.name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: left ? '#777' : '#0D0D0D' }}>{contact.name}</span>
                {primary && (
                  <span style={PRIMARY_TAG} title={`Primary contact for ${homeName}`}>
                    Primary
                  </span>
                )}
                {left && (
                  <span style={LEFT_TAG} title="Left the company - kept for history, hidden from task and attendee pickers">
                    {LEFT_COMPANY}
                  </span>
                )}
              </div>
              {(title || contactRole) && (
                <div style={{ fontSize: 11, color: '#888', display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                  {title && <span>{title}</span>}
                  {title && contactRole && <span>·</span>}
                  {contactRole && <span style={{ color: '#6A4C93', fontWeight: 700 }}>{contactRole}</span>}
                </div>
              )}
              <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#666', flexWrap: 'wrap', alignItems: 'center' }}>
                {email && (
                  <a
                    href={`mailto:${email}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#1B5E20', fontWeight: 600, textDecoration: 'none' }}
                  >
                    <Icon name="mail" size={11} color="#bbb" />
                    {email}
                  </a>
                )}
                {phone && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="phone" size={11} color="#bbb" />
                    {phone}
                  </span>
                )}
                {linkedin && (
                  <a
                    href={linkedinHref(linkedin)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 10.5, fontWeight: 700, color: '#0A66C2' }}
                  >
                    LinkedIn ↗
                  </a>
                )}
                {!email && !phone && !linkedin && <span style={{ color: '#bbb' }}>No email, phone or LinkedIn on file</span>}
              </div>
              <div style={{ display: 'flex', gap: 6, fontSize: 11, color: '#888', marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <Icon name="mappin" size={11} color="#bbb" />
                {home && onOpenAccount ? (
                  <button onClick={() => openAcct(home)} title={`Open ${home.name}`} style={{ ...linkBtn, fontSize: 11 }}>
                    {home.name}
                  </button>
                ) : (
                  <span style={{ fontWeight: 700, color: '#444' }}>{home ? home.name : homeName}</span>
                )}
                {parent && (
                  <>
                    <span>· site of</span>
                    {onOpenAccount ? (
                      <button onClick={() => openAcct(parent)} title={`Open ${parent.name}`} style={{ ...linkBtn, fontSize: 11 }}>
                        {parent.name}
                      </button>
                    ) : (
                      <span style={{ fontWeight: 700, color: '#444' }}>{parent.name}</span>
                    )}
                  </>
                )}
              </div>
            </div>
            {editable && !editingForm && (
              <button
                onClick={() => {
                  setEditingForm({ ...contactFormFrom(contact), accountId: homeId || '' });
                  setEditErr('');
                }}
                title="Edit or move this contact"
                style={{ ...BTN_PRIMARY, padding: '6px 12px', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Icon name="edit" size={10} color="#fff" /> Edit
              </button>
            )}
          </div>
          {left && (
            <div style={{ marginTop: 12, fontSize: 10.5, color: '#616161', background: '#F5F5F5', borderRadius: 6, padding: '6px 10px' }}>
              Left the company. Their history stays here, and they are no longer offered as a task contact or a meeting attendee.
            </div>
          )}
          {editingForm && (
            <div style={{ background: '#FAFBFC', borderRadius: 10, padding: 18, marginTop: 16, border: '1px solid #F0F0F0' }}>
              <ContactFields
                form={editingForm}
                setForm={setEditingForm}
                accounts={allAccounts}
                picker={{ label: 'Account / site (Move to...)', placeholder: 'Move to...', first: false }}
                primaryLabel="Primary contact for this account / site"
                onEdited={() => setEditErr('')}
              />
              {editErr && <div style={{ ...ERROR_LINE, marginTop: 10 }}>{editErr}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={saveEdit} disabled={saving} style={{ ...BTN_PRIMARY, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setEditingForm(null);
                    setEditErr('');
                  }}
                  style={BTN_CANCEL}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* TASKS that name this contact (they live on a lead site or an opportunity) */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={cardTitle}>
              Tasks naming {contact.name}
              {open.length > 0 && (
                <span style={{ marginLeft: 6, background: '#1B5E20', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 9 }}>
                  {open.length} open
                </span>
              )}
            </span>
          </div>
          <div style={{ fontSize: 9.5, color: '#aaa', marginBottom: 8 }}>
            Tasks live on a lead site or an opportunity and can name a contact. Add one from that record.
          </div>
          {tasksErr && <div style={{ ...ERROR_LINE, marginBottom: 8 }}>{tasksErr}</div>}
          {!tasksErr && tasksLoaded && open.length === 0 && (
            <div style={{ fontSize: 11, color: '#aaa' }}>
              {done.length ? 'No open tasks name this contact.' : 'No tasks name this contact yet.'}
            </div>
          )}
          {!tasksLoaded && <div style={{ fontSize: 11, color: '#aaa' }}>Loading tasks...</div>}
          {open.map((t) => {
            const overdue = !!dayOf(t.due_date) && dayOf(t.due_date) < today;
            return (
              <div
                key={t.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '7px 0',
                  borderBottom: '1px solid #F4F4F0',
                  flexWrap: 'wrap',
                }}
              >
                {editable && (
                  <input type="checkbox" title="Mark complete" checked={false} onChange={() => complete(t)} style={{ cursor: 'pointer' }} />
                )}
                <span style={{ flex: 1, minWidth: 160, fontSize: 11.5, color: '#0D0D0D', fontWeight: 600 }}>{t.description}</span>
                {homeLink(t)}
                <span style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>{t.owner}</span>
                <TaggedNames names={taggedOf(t)} style={{ fontSize: 10 }} />
                <span style={{ fontSize: 10, fontWeight: 800, color: overdue ? '#B71C1C' : '#1565C0', whiteSpace: 'nowrap' }}>
                  {overdue ? 'OVERDUE · ' : 'due '}
                  {fmtDate(t.due_date) || '-'}
                </span>
              </div>
            );
          })}
          {done.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setShowDone(!showDone)} style={{ ...linkBtn, fontSize: 9.5, color: '#888' }}>
                {showDone ? '▾' : '▸'} {done.length} completed / cancelled
              </button>
              {showDone &&
                done.map((t) => (
                  <div
                    key={t.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '5px 0',
                      borderBottom: '1px solid #F7F7F4',
                      opacity: 0.8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 800,
                        color: t.status === 'complete' ? '#1B5E20' : '#999',
                        textTransform: 'uppercase',
                        width: 64,
                      }}
                    >
                      {t.status === 'complete' ? 'done' : 'cancelled'}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 160,
                        fontSize: 11,
                        color: '#555',
                        textDecoration: t.status === 'complete' ? 'line-through' : 'none',
                      }}
                    >
                      {t.description}
                    </span>
                    {homeLink(t)}
                    <span style={{ fontSize: 9.5, color: '#999', whiteSpace: 'nowrap' }}>{t.owner}</span>
                    <TaggedNames names={taggedOf(t)} style={{ fontSize: 9.5, color: '#999' }} />
                    <span style={{ fontSize: 9.5, color: '#999', whiteSpace: 'nowrap' }}>
                      {t.status === 'complete' && t.completed_at
                        ? `completed ${fmtDate(localDayOf(t.completed_at))}`
                        : `was due ${fmtDate(t.due_date) || '-'}`}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* ACTIVITY: everything logged with this contact, plus their tasks' events */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={cardTitle}>Activity</span>
            {editable && home && !left && !logging && (
              <button
                onClick={() => setLogging(true)}
                style={{ ...BTN_PRIMARY, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <Icon name="plus" size={11} color="#fff" /> Log Activity
              </button>
            )}
          </div>
          {logging && home && (
            <div>
              <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
                Logged on {homeName}, with {contact.name} as an attendee.
              </div>
              <ActivityForm
                record={home}
                defaults={{ contact_ids: [id], opportunity_ids: [] }}
                companyWithSites={homeHasSites}
                onSaved={onLogged}
                onCancel={() => setLogging(false)}
              />
            </div>
          )}
          <Timeline kind="contact" id={id} refreshKey={refreshKey} editable={editable} deletable={deletable} onChanged={() => fetchAll()} />
        </div>
      </div>
      {prompt}
    </div>
  );
}
